/**
 * Crease-band reweight — ADR-028 E1's weight half: transfer limb-chain skin
 * weight to the half-rotation helper across a band around the hinge joint, so
 * the bend interpolates across two half-rotations instead of one chord.
 *
 * The taper is triangular: at the joint plane the helper takes ALL of the
 * chain's weight (skin there follows factor·θ exactly — the double-jointed
 * doctrine's midpoint), fading linearly to zero at ±bandWorld. Vertices are
 * selected on their BIND positions via the mesh's stored bindMatrix — the
 * pose-independent frame (the elbow-evidence law: a playing clip must not
 * move the measurement).
 *
 * Weight LAWS, same as the wrist re-own (bind-hand.ts): only chain slots
 * (parent, driver, and their twist ladders) are taken from — flesh weighted
 * elsewhere is never touched; partition of unity is preserved by construction
 * (weight moves between slots, never appears or vanishes) and MEASURED anyway
 * (worstWeightSumError); the four-slot ceiling evicts the weakest, counted
 * and reported, never silent.
 *
 * Depends on: three, kernel (ResolvedHinge). Depended on by: the Document
 * driver's hinge pass, strictly after the helper is seated in the skeleton.
 */
import { Matrix4, Vector3, type SkinnedMesh } from 'three';
import type { ResolvedHinge, ResolvedLeaf, RigDescriptor } from '@harryhausen/kernel';
import { seatInfluences } from '../weights/seat-influences.js';

export interface HingeReweightReport {
  readonly helper: string;
  readonly verticesReweighted: number;
  /**
   * Full four-slot vertices where the receiver's share would not outrank the
   * weakest existing influence — left UNTOUCHED (don't move what you cannot
   * seat; seat-influences). Expected at taper rims; counted, never silent.
   */
  readonly verticesUnseated: number;
  readonly influencesEvicted: number;
  readonly largestEviction: number;
  readonly worstWeightSumError: number;
}

export class HingeReweightError extends Error {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot reweight hinge band (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/**
 * Reweight one hinge's crease band. The helper must already be in the mesh's
 * skeleton (seat it via bindGraftedDigits with an empty influence list — the
 * one seat, reused, per ADR-028 §4).
 */
export function reweightHingeBand(mesh: SkinnedMesh, rig: RigDescriptor, hinge: ResolvedHinge): HingeReweightReport {
  const issues: string[] = [];
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  const position = mesh.geometry.getAttribute('position');
  if (!skinIndex || !skinWeight || !position) {
    throw new HingeReweightError(['mesh is not skinned (no skinIndex/skinWeight/position)']);
  }
  const slotOf = new Map(mesh.skeleton.bones.map((b, i) => [b.name, i] as const));
  const helperSlot = slotOf.get(hinge.helper);
  if (helperSlot === undefined) {
    throw new HingeReweightError([`helper "${hinge.helper}" is not in the skeleton — seat it before reweighting`]);
  }
  const boneNames = mesh.skeleton.bones.map((b) => b.name);
  const isChain = (name: string): boolean =>
    name === hinge.driver ||
    name === hinge.parent ||
    name.startsWith(`${hinge.driver}Twist`) ||
    name.startsWith(`${hinge.parent}Twist`);

  const driverBone = rig.bones.find((b) => b.name === hinge.driver);
  const endBone = rig.bones.find((b) => b.name === hinge.end);
  if (driverBone === undefined || endBone === undefined) {
    throw new HingeReweightError([`rig lacks "${hinge.driver}" or "${hinge.end}" — the resolved hinge and the rig disagree`]);
  }
  const jp = driverBone.worldBind.position;
  const ep = endBone.worldBind.position;
  const joint = new Vector3(jp.x, jp.y, jp.z);
  const limb = new Vector3(ep.x, ep.y, ep.z).sub(joint).normalize();

  const bind = new Matrix4().copy(mesh.bindMatrix);
  const influencesPerVertex = skinIndex.itemSize;
  const v = new Vector3();
  let verticesReweighted = 0;
  let verticesUnseated = 0;
  let influencesEvicted = 0;
  let largestEviction = 0;
  let worstWeightSumError = 0;

  for (let i = 0; i < position.count; i += 1) {
    v.fromBufferAttribute(position, i).applyMatrix4(bind);
    const along = v.sub(joint).dot(limb);
    if (Math.abs(along) > hinge.bandWorld) continue;
    const f = 1 - Math.abs(along) / hinge.bandWorld;

    // Rebuild this vertex's slot→weight view from the live attributes.
    const slots = new Map<number, number>();
    let chainWeight = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w <= 0) continue;
      const slot = skinIndex.getComponent(i, k);
      slots.set(slot, (slots.get(slot) ?? 0) + w);
      if (isChain(boneNames[slot] ?? '')) chainWeight += w;
    }
    if (chainWeight <= 0) continue; // not this limb's flesh — never touched

    // Transfer f of each chain slot's weight to the helper.
    let transferred = 0;
    for (const [slot, w] of slots) {
      if (!isChain(boneNames[slot] ?? '')) continue;
      const take = w * f;
      if (take <= 0) continue;
      slots.set(slot, w - take);
      transferred += take;
    }
    if (transferred <= 0) continue;
    slots.set(helperSlot, (slots.get(helperSlot) ?? 0) + transferred);

    // Four-slot ceiling: don't move what you cannot seat (seat-influences) —
    // at the band's rim the helper's share is the smallest of five; the
    // vertex stays as painted until the share outranks the weakest.
    const { kept: top, evicted, unseatedReceivers } = seatInfluences(slots, new Set([helperSlot]), influencesPerVertex);
    if (unseatedReceivers.length > 0) {
      verticesUnseated += 1;
      continue;
    }
    influencesEvicted += evicted.length;
    for (const [, w] of evicted) largestEviction = Math.max(largestEviction, w);
    const total = top.reduce((s, [, w]) => s + w, 0);
    if (total <= 0) {
      issues.push(`vertex ${i}: reweight left zero total weight — refusing to emit an unskinned vertex`);
      continue;
    }
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const entry = top[k];
      skinIndex.setComponent(i, k, entry === undefined ? 0 : entry[0]);
      skinWeight.setComponent(i, k, entry === undefined ? 0 : entry[1] / total);
    }
    let sum = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) sum += skinWeight.getComponent(i, k);
    worstWeightSumError = Math.max(worstWeightSumError, Math.abs(sum - 1));
    verticesReweighted += 1;
  }
  if (issues.length > 0) throw new HingeReweightError(issues);
  skinIndex.needsUpdate = true;
  skinWeight.needsUpdate = true;

  return { helper: hinge.helper, verticesReweighted, verticesUnseated, influencesEvicted, largestEviction, worstWeightSumError };
}

/**
 * E2's compartment sibling of the crease band: transfer a share of the
 * CHAIN's weight to a muscle leaf across the belly's ANATOMICAL COMPARTMENT —
 * an axial band along the segment intersected with the displacement
 * direction's half-space, RADIALLY UNBOUNDED. A sphere from the bone line
 * cannot reach the skin of a thick arm (measured on amari: flesh ~7cm off a
 * 14.8cm humerus — a 0.28-fraction radius caught 38 of ~155 belly vertices
 * and the swell read zero); girth must never defeat the region, which is
 * STAR's lesson applied to membership. The half-space keeps the antagonist
 * compartment (the tricep behind the bicep) untouched. Taper is axial. Same
 * weight laws as the band: bind-space selection, chain slots only, partition
 * preserved and measured, evictions counted.
 */
/**
 * The belly COMPARTMENT, one definition for every reader: axial band along
 * the segment ∧ the displacement half-space ∧ chain-weighted flesh. Calls
 * `fn` per member with its axial offset `t`, its radial distance from the
 * segment axis, and its slot→weight view. The reweight and the girth
 * measure agree on membership by construction, never by parallel code.
 */
function forEachBellyVertex(
  mesh: SkinnedMesh,
  hinge: ResolvedHinge,
  leaf: ResolvedLeaf,
  fn: (i: number, t: number, radialLength: number, slots: Map<number, number>) => void,
  options: { readonly halfSpace: boolean } = { halfSpace: true },
): void {
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  const position = mesh.geometry.getAttribute('position');
  if (!skinIndex || !skinWeight || !position) {
    throw new HingeReweightError(['mesh is not skinned (no skinIndex/skinWeight/position)']);
  }
  const boneNames = mesh.skeleton.bones.map((b) => b.name);
  // The crease band runs FIRST and moves chain weight onto the half-rotation
  // helper; where a belly overlaps the band (the forearm flexor over the
  // proximal forearm does) that share is still this limb's flesh, so the
  // helper is chain weight for the belly's purposes — otherwise the leaf
  // takes only the residual and the swell is suppressed across the overlap
  // (Codex P2, #107 round five).
  const isChain = (name: string): boolean =>
    name === hinge.driver ||
    name === hinge.parent ||
    name === hinge.helper ||
    name.startsWith(`${hinge.driver}Twist`) ||
    name.startsWith(`${hinge.parent}Twist`);
  const site = new Vector3(leaf.siteWorld.x, leaf.siteWorld.y, leaf.siteWorld.z);
  const axis = new Vector3(leaf.axisWorld.x, leaf.axisWorld.y, leaf.axisWorld.z);
  const dir = new Vector3(leaf.dirWorld.x, leaf.dirWorld.y, leaf.dirWorld.z);
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const influencesPerVertex = skinIndex.itemSize;
  const v = new Vector3();
  const radial = new Vector3();
  for (let i = 0; i < position.count; i += 1) {
    v.fromBufferAttribute(position, i).applyMatrix4(bind).sub(site);
    const t = v.dot(axis);
    if (Math.abs(t) > leaf.axialWorld) continue;
    // The compartment half-space: radial offset from the segment axis must
    // point WITH the displacement — the antagonist side never moves.
    radial.copy(v).addScaledVector(axis, -t);
    if (options.halfSpace && radial.dot(dir) <= 0) continue;
    const slots = new Map<number, number>();
    let chainWeight = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w <= 0) continue;
      const slot = skinIndex.getComponent(i, k);
      slots.set(slot, (slots.get(slot) ?? 0) + w);
      if (isChain(boneNames[slot] ?? '')) chainWeight += w;
    }
    if (chainWeight <= 0) continue; // not this limb's flesh — never touched
    fn(i, t, radial.length(), slots);
  }
}

export interface LeafGirthReading {
  readonly leaf: string;
  /** Mean radial distance of the compartment's skin from the segment axis at bind, world units. */
  readonly radiusWorld: number;
  readonly vertices: number;
}

/**
 * The belly's GIRTH at bind — the amplitude scale ADR-028 §6 requires
 * ("amplitude must scale with girth; per-character amplitude is measured at
 * ingest from the same bind the graft measures"). The same axial band and
 * chain-weight law as the reweight, read before any weight moves, over the
 * FULL ring rather than the displacement half-space: Tripo's limb joints sit
 * ~2 cm anterior of the flesh centroid, so a half-space radius reads the
 * thin side's thickness, not the limb's (measured: Sergio's forearm flexor
 * compartment 1.3 cm anterior vs ~4 cm of forearm). The ring's mean radius
 * is the girth, robust to that offset. Null when the band holds too little
 * flesh to name a radius (the caller keeps the segment length and says so —
 * never a silent fallback).
 */
export function measureLeafGirth(mesh: SkinnedMesh, hinge: ResolvedHinge, leaf: ResolvedLeaf): LeafGirthReading | null {
  let sum = 0;
  let n = 0;
  forEachBellyVertex(
    mesh,
    hinge,
    leaf,
    (_i, _t, radialLength) => {
      sum += radialLength;
      n += 1;
    },
    { halfSpace: false },
  );
  if (n < 12) return null;
  return { leaf: leaf.name, radiusWorld: sum / n, vertices: n };
}

export function reweightMuscleBelly(mesh: SkinnedMesh, hinge: ResolvedHinge, leaf: ResolvedLeaf): HingeReweightReport {
  const issues: string[] = [];
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!skinIndex || !skinWeight) {
    throw new HingeReweightError(['mesh is not skinned (no skinIndex/skinWeight/position)']);
  }
  const slotOf = new Map(mesh.skeleton.bones.map((b, i) => [b.name, i] as const));
  const leafSlot = slotOf.get(leaf.name);
  if (leafSlot === undefined) {
    throw new HingeReweightError([`leaf "${leaf.name}" is not in the skeleton — seat it before reweighting`]);
  }
  const boneNames = mesh.skeleton.bones.map((b) => b.name);
  // The crease band runs FIRST and moves chain weight onto the half-rotation
  // helper; where a belly overlaps the band (the forearm flexor over the
  // proximal forearm does) that share is still this limb's flesh, so the
  // helper is chain weight for the belly's purposes — otherwise the leaf
  // takes only the residual and the swell is suppressed across the overlap
  // (Codex P2, #107 round five).
  const isChain = (name: string): boolean =>
    name === hinge.driver ||
    name === hinge.parent ||
    name === hinge.helper ||
    name.startsWith(`${hinge.driver}Twist`) ||
    name.startsWith(`${hinge.parent}Twist`);
  const influencesPerVertex = skinIndex.itemSize;
  let verticesReweighted = 0;
  let verticesUnseated = 0;
  let influencesEvicted = 0;
  let largestEviction = 0;
  let worstWeightSumError = 0;

  forEachBellyVertex(mesh, hinge, leaf, (i, t, _radialLength, slots) => {
    const f = leaf.weightShare * (1 - Math.abs(t) / leaf.axialWorld);

    let transferred = 0;
    for (const [slot, w] of slots) {
      if (!isChain(boneNames[slot] ?? '')) continue;
      const take = w * f;
      if (take <= 0) continue;
      slots.set(slot, w - take);
      transferred += take;
    }
    if (transferred <= 0) return;
    slots.set(leafSlot, (slots.get(leafSlot) ?? 0) + transferred);

    // Don't move what you cannot seat (seat-influences): at the compartment's
    // axial edge the leaf's share is the smallest of five; the vertex stays
    // as painted until the share outranks the weakest.
    const { kept: top, evicted, unseatedReceivers } = seatInfluences(slots, new Set([leafSlot]), influencesPerVertex);
    if (unseatedReceivers.length > 0) {
      verticesUnseated += 1;
      return;
    }
    influencesEvicted += evicted.length;
    for (const [, w] of evicted) largestEviction = Math.max(largestEviction, w);
    const total = top.reduce((s, [, w]) => s + w, 0);
    if (total <= 0) {
      issues.push(`vertex ${i}: belly reweight left zero total weight — refusing to emit an unskinned vertex`);
      return;
    }
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const entry = top[k];
      skinIndex.setComponent(i, k, entry === undefined ? 0 : entry[0]);
      skinWeight.setComponent(i, k, entry === undefined ? 0 : entry[1] / total);
    }
    let sum = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) sum += skinWeight.getComponent(i, k);
    worstWeightSumError = Math.max(worstWeightSumError, Math.abs(sum - 1));
    verticesReweighted += 1;
  });
  if (issues.length > 0) throw new HingeReweightError(issues);
  skinIndex.needsUpdate = true;
  skinWeight.needsUpdate = true;

  return { helper: leaf.name, verticesReweighted, verticesUnseated, influencesEvicted, largestEviction, worstWeightSumError };
}
